import type { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useSelectionStore } from '@/state/selection';
import { useTourStore } from '@/state/tour';
import { instanceMesh, LayerBatch, type ElementEntry } from './batch';

export function pickedElement(hit: {
  object: THREE.Object3D;
  batchId?: number;
}): ElementEntry | null {
  const batch = hit.object;
  if (!(batch instanceof LayerBatch) || hit.batchId == null) return null;
  return batch.elements.get(batch.expressIDs[hit.batchId] ?? -1) ?? null;
}

// The outline pass needs whole objects, so the hovered element gets invisible copies of its
// instances that the pass can select.
const OUTLINE_PROXY = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false });
let hovered: { el: ElementEntry; proxies: THREE.Mesh[] } | null = null;

export function hoverElement(el: ElementEntry | null): void {
  if (hovered?.el === el) return;
  if (hovered) {
    for (const p of hovered.proxies) {
      p.removeFromParent();
      p.geometry.dispose();
    }
  }
  hovered = el ? { el, proxies: el.instances.map((i) => instanceMesh(i, OUTLINE_PROXY)) } : null;
  useSelectionStore.getState().setHoverMeshes(hovered?.proxies ?? []);
}

// Pointer handlers for one layer's batches: hover card, outline and click to select.
export function batchPointer(
  layer: string,
  pickable: () => boolean,
): {
  onPointerMove: (e: ThreeEvent<PointerEvent>) => void;
  onPointerOut: () => void;
  onClick: (e: ThreeEvent<MouseEvent>) => void;
} {
  return {
    onPointerMove(e) {
      if (!pickable()) return;
      e.stopPropagation();
      const el = pickedElement(e);
      if (!el) return;
      useTourStore.getState().setHovered({
        layer,
        expressID: el.expressID,
        type: el.type,
        name: el.name,
        x: e.nativeEvent.clientX,
        y: e.nativeEvent.clientY,
      });
      hoverElement(el);
    },
    onPointerOut() {
      useTourStore.getState().setHovered(null);
      hoverElement(null);
    },
    onClick(e) {
      if (!pickable()) return;
      e.stopPropagation();
      const el = pickedElement(e);
      if (el) useSelectionStore.getState().select({ layer, expressID: el.expressID });
    },
  };
}
